/**
 * 💝 useCheer Hook
 * 격려 메시지 전송, 수신, 조회를 위한 커스텀 훅
 * Firestore의 'cheers' 컬렉션을 사용합니다
 */

import { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../firebase';
import {
    collection,
    addDoc,
    query,
    where,
    orderBy,
    onSnapshot,
    serverTimestamp,
    limit,
    getDocs,
    updateDoc,
    doc
} from 'firebase/firestore';
import { getCheerMessageById } from '../constants/cheerMessages';

export default function useCheer(userProfile) {
    const [receivedCheers, setReceivedCheers] = useState([]);
    const [sentCheers, setSentCheers] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);

    const organizationId = userProfile?.organizationId;
    const currentUserId = auth.currentUser?.uid;

    // 받은 격려 실시간 구독
    useEffect(() => {
        if (!organizationId || !currentUserId) {
            setReceivedCheers([]);
            setLoading(false);
            return;
        }

        const cheersRef = collection(db, 'organizations', organizationId, 'cheers');
        const q = query(
            cheersRef,
            where('recipientId', '==', currentUserId),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const cheers = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate?.() || new Date()
            }));
            setReceivedCheers(cheers);
            setUnreadCount(cheers.filter(c => !c.isRead).length);
            setLoading(false);
        }, (error) => {
            // Firestore composite index 필요시 조용히 처리
            if (error.code === 'failed-precondition' || error.message?.includes('index')) {
                console.info('[useCheer] Firestore 인덱스가 필요합니다. 격려 기능이 제한될 수 있습니다.');
            } else {
                console.error('격려 로드 오류:', error);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [organizationId, currentUserId]);

    // 보낸 격려 로드
    useEffect(() => {
        if (!organizationId || !currentUserId) {
            setSentCheers([]);
            return;
        }

        const loadSentCheers = async () => {
            try {
                const cheersRef = collection(db, 'organizations', organizationId, 'cheers');
                const q = query(
                    cheersRef,
                    where('senderId', '==', currentUserId),
                    orderBy('createdAt', 'desc'),
                    limit(50)
                );
                const snapshot = await getDocs(q);
                const cheers = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    createdAt: doc.data().createdAt?.toDate?.() || new Date()
                }));
                setSentCheers(cheers);
            } catch (error) {
                // Firestore composite index 필요시 조용히 처리
                if (error.code === 'failed-precondition' || error.message?.includes('index')) {
                    console.info('[useCheer] Firestore 인덱스가 필요합니다.');
                } else {
                    console.error('보낸 격려 로드 오류:', error);
                }
            }
        };

        loadSentCheers();
    }, [organizationId, currentUserId]);

    // 격려 보내기
    const sendCheer = useCallback(async (cheerData) => {
        if (!organizationId || !currentUserId || !userProfile) {
            throw new Error('로그인이 필요합니다');
        }

        const cheersRef = collection(db, 'organizations', organizationId, 'cheers');

        const newCheer = {
            messageId: cheerData.messageId,
            message: cheerData.message,
            emoji: cheerData.emoji,
            category: cheerData.category,
            senderId: currentUserId,
            senderName: userProfile.name || '익명',
            senderPhoto: userProfile.photoURL || null,
            recipientId: cheerData.recipientId,
            recipientName: cheerData.recipientName,
            isRead: false,
            createdAt: serverTimestamp()
        };

        const docRef = await addDoc(cheersRef, newCheer);

        console.log('[Cheer] 격려 전송 완료:', docRef.id);

        // [NEW] 알림 생성 (NotificationContext와 연동)
        // notifications 컬렉션에 직접 추가하여 알림 센터에 표시되게 함
        // [MOD] visibleTo 필드 추가: 격려 알림은 보낸 사람과 받는 사람에게만 표시
        try {
            const notificationsRef = collection(db, 'notifications');
            await addDoc(notificationsRef, {
                title: '새로운 💝 격려가 도착했습니다!',
                message: `${userProfile.name}님이 격려를 보냈어요: "${cheerData.message}"`,
                type: 'info',
                category: 'cheer',
                targetId: docRef.id, // 격려 메시지 ID
                organizationId: organizationId,
                createdBy: currentUserId,
                createdAt: serverTimestamp(),
                readBy: [], // 읽지 않음 상태
                visibleTo: [currentUserId, cheerData.recipientId] // [NEW] 보낸 사람과 받는 사람만 볼 수 있음
            });
        } catch (error) {
            console.error('[Cheer] 알림 생성 실패:', error);
            // 알림 실패가 격려 전송 실패는 아니므로 에러 무시
        }

        return docRef.id;
    }, [organizationId, currentUserId, userProfile]);

    // 격려 읽음 처리
    const markAsRead = useCallback(async (cheerId) => {
        if (!organizationId) return;

        try {
            const cheerRef = doc(db, 'organizations', organizationId, 'cheers', cheerId);
            await updateDoc(cheerRef, { isRead: true });
        } catch (error) {
            console.error('읽음 처리 오류:', error);
        }
    }, [organizationId]);

    // 전체 읽음 처리
    const markAllAsRead = useCallback(async () => {
        if (!organizationId) return;

        try {
            const unreadCheers = receivedCheers.filter(c => !c.isRead);
            await Promise.all(
                unreadCheers.map(cheer =>
                    updateDoc(doc(db, 'organizations', organizationId, 'cheers', cheer.id), { isRead: true })
                )
            );
        } catch (error) {
            console.error('전체 읽음 처리 오류:', error);
        }
    }, [organizationId, receivedCheers]);

    // 통계
    const stats = {
        totalReceived: receivedCheers.length,
        totalSent: sentCheers.length,
        unreadCount,
        // 카테고리별 받은 격려 수
        categoryStats: receivedCheers.reduce((acc, cheer) => {
            acc[cheer.category] = (acc[cheer.category] || 0) + 1;
            return acc;
        }, {}),
    };

    return {
        receivedCheers,
        sentCheers,
        unreadCount,
        loading,
        sendCheer,
        markAsRead,
        markAllAsRead,
        stats
    };
}
